import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux';
import './eval-form.styles.scss'
import ThreeDotsLoader from '../../SHARED/loader/three-dots-loader.component';
import { useNavigate, useParams } from 'react-router-dom';
import { ModalAlert, ModalConfirm } from '../../modal/modal.component';
import { getQueryFromFirestore, getUniqueDocumentFromFirestore } from '../../../utils/getDataFromDbSimplified';
import { Sources } from '../../../forms/formTypes';
import EvalQuestion from './eval-question.component';
import { UseCloudFunction } from '../../../utils/firebase.utils';
import { RandomString } from '../../../utils/randomString';
import { obfuscateString } from '../../../utils/obfuscation';
import FullPageLoader from '../../SHARED/loader/full-page-loader.component';

const EvalForm = () => {

  const screenSize = useSelector(state => state.listsData.screenSize);
  const userProfile = useSelector(state => state.userData.userProfile);
  const [evalData, setEvalData] = useState(null);
  const [questionSets, setQuestionSets] = useState(null);
  const [formData, setFormData] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [ US_STATES, setUS_STATES] = useState([]);
  const [ loading, setLoading ] = useState(true);
  const dataRef = useRef({});

  function saveData(questionId, value) {
    if (value === null) {
      const next = { ...dataRef.current };
      delete next[questionId];
      dataRef.current = next;
    } else {
      dataRef.current = { ...dataRef.current, [questionId]: value };
    }
    setFormData(dataRef.current);
  }
  const { evalPath } = useParams();
  const approveRef = useRef();
  const qSetRef = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    const userTimeout = setTimeout(() => {
      console.log('userProfile', userProfile);
      if (!userProfile) {
        navigate('/');
        return;
      } else {
        localStorage.removeItem('evalPath');
      }
    }, 1000);
    return () => {
      clearTimeout(userTimeout);
    };
  }, [])

  useEffect(() => {
    console.log('evalPath', evalPath)
    if (!evalPath) return;
    getEvalData();
    getUSStates();
  }, [evalPath])

  function getUSStates() {
    getUniqueDocumentFromFirestore({
      'path': 'utils/stateEvidenceMap',
      'callback': callback,
      'once': true
    })
    function callback(data) {
      setUS_STATES(data || {});
    }
  }

  async function getEvalData() {
    const getEval = await UseCloudFunction(
      'getEvalCheckCompletionV2',
      {
        'cst_key': userProfile.id,
        'evalPath': evalPath
      }
    )
    console.log(getEval);
    if (getEval.evalData) {
      setEvalData(getEval.evalData);
      getQuestionSets(getEval.evalData);
    } else if (getEval.message === 'already completed') {
      setAlertMessage('Sorry, you have already completed this evaluation. Check your list of completed evaluations to make sure this is correct.')
      approveRef.current = () => {
        setAlertMessage(null);
        // navigate('/home');
        return;
      }

    } else if (getEval.message === 'no eval exists') {
      setAlertMessage('Sorry, this evaluation link does not match with any of our existing evaluations.')
      approveRef.current = () => {
        setAlertMessage(null);
        // navigate('/home');
        return;
      }
    } else {
      setAlertMessage('There is no evaluation associated with this link. Please check the link and try again. If you continue to have issues, please contact support at <a href="mailto:support@edleadersnetwork.com" target="_blank">support@edleadersnetwork.com</a>.');
      approveRef.current = () => {
        setAlertMessage(null);
        // navigate('/home');
        return;
      }
    }
  }


  function getQuestionSets(evalDoc) {
    qSetRef.current = [];
    setQuestionSets(null);
    for (let qSetId of evalDoc.questionSets) {
      getUniqueDocumentFromFirestore({
        'path': `forms_data/${qSetId}`,
        'once': true,
        'callback': callback
      })
      function callback(data) {
        console.log(data);
        qSetRef.current.push(data);
        setQuestionSets([...qSetRef.current]);
        setLoading(false);
      }
    }
  }

  function submitEval() {
    let tempData = { ...dataRef.current };
    const reqArr = [];
    if (evalData.cpdu) {
      if (!tempData.startDate) reqArr.push('Start Date of Experience (if experience was only one day, put the same date as the start and end dates)');
      if (!tempData.date) reqArr.push('End Date of Experience (if experience was only one day, put the same date as the start and end dates).');
      if (!tempData.state) reqArr.push('Your State');
    }
    for (let questionSet of questionSets) {
      for (let question of Object.values(questionSet.questions)) {
        if (!question.required) continue;
        let answered;
        if (question.type === 'checkbox' || question.type === 'multiple_choice') {
          // radio/checkbox: option IDs are keys; select: question.id is the key
          answered = (question.options || []).some(o => tempData[o.id]) || !!tempData[question.id];
        } else {
          answered = !!tempData[question.id];
        }
        if (!answered) reqArr.push(question.prompt || question.id);
      }
    }
    if (reqArr.length > 0) {
      setAlertMessage(`Please answer all required questions before submitting.<br/><ul>${reqArr.map(p => `<li>${p}</li>`).join('')}</ul>`);
      return;
    }
    setConfirmMessage('Are you sure you want to submit your evaluation? Once submitted, you will not be able to make changes.');
    approveRef.current = () => {
      continueSubmit();
    }

    async function continueSubmit() {
      setLoading(true);
      const now = new Date().getTime();
      tempData = {
        ...tempData,
        ...{
          'evalId': evalData.id,
          'evalSource': evalData.source,
          'evalType': 'eln',
        }
      }
      if (evalData.itemId) {
        tempData.evalItemId = evalData.itemId;
      }
      console.log(tempData);
      const res = await UseCloudFunction(
        'saveUserEval',
        {
          'formData': tempData,
          'evalData': evalData,
          'platform': 'eln',
          'customEval': evalData.customEval ? evalData.customEval : false,
          'userProfile': userProfile,
          'containsEval': true
        }
      )
      if (res.message === 'Eval saved') {
        let text = `Your evaluation has been saved successfully!`
        if (evalData.cpdu) {
          text += ` When you click "Ok", you will be redirected to the evidence document for your state. ` +
            `If you go back to <a href="https://eval.edleadersnetwork.org">eval.edleadersnetwork.org</a>, you can access this or any other evidence of completion documents.`
        }
        setAlertMessage(text)
        approveRef.current = () => {
          navigate('/home');
          // if (evalData.cpdu) {
          //   const urlString = obfuscateString(
          //     JSON.stringify({
          //       'compId': res.completedEvalObj.id,
          //       'cstKey': res.completedEvalObj.userId
          //     })
          //   );
          //   window.open(
          //     `${window.location.origin}/print/${urlString}`,
          //     '_blank',
          //     'noopener,noreferrer'
          //   )
          // } else {
          //   navigate('/home');
          // }
        }
      } else {
        setAlertMessage('Something went wrong, please try again in a few minutes.')
      }
      setLoading(false);
    }
  }

  return (
    <div className={`eval-form-container ${screenSize}`}>
      <div className={`eval-form-card ${screenSize === 'mobile' ? 'mobile' : 'g-card'}`}>
        {
          (evalData)
            ?
            <div>
              <h3>
                <span className='meta'>Evaluation:</span>
                <br />
                <b>{evalData?.item?.title}</b>
              </h3>
              <hr />
              <h4>
                <b>Title:</b> {evalData?.item?.title}<br />
                <b>Training Type:</b> {
                  (Sources)
                    ? (Sources[evalData?.source]?.title || evalData?.source)
                    : evalData?.item?.source
                }
              </h4>
              <hr />
              {
                (evalData && evalData.cpdu)
                  ?
                  <div>
                    <p>
                      This evaluation will not be associated with your personal
                      information in any way. Your email will be used only so we
                      can send you the evidence of completion document for your
                      records. If you are an Illinois educator, you will need to
                      import the information from this document into ELIS manually.
                    </p>
                    <p>
                      All questions are required unless otherwise noted.
                    </p>
                    <div className={`g-triple-col ${screenSize}`}>
                      <div className='field'>
                        <label className='section-label'>
                          Start Date of Experience
                        </label>
                        <input
                          className="uk-input"
                          type="date"
                          onChange={e => {
                            const [y, m, d] = e.target.value.split('-').map(Number);
                            saveData('startDate', new Date(y, m - 1, d, 12).getTime());
                          }} />
                      </div>
                      <div className='field'>
                        <label className='section-label'>
                          End Date of Experience
                        </label>
                        <input
                          className="uk-input"
                          type="date"
                          onChange={e => {
                            const [y, m, d] = e.target.value.split('-').map(Number);
                            saveData('date', new Date(y, m - 1, d, 12).getTime());
                          }} />
                      </div>

                      <div className='field'>
                        <label className='section-label'>
                          Your State
                        </label>
                        <select
                          className="uk-select"
                          onChange={(e) =>
                            saveData('state', e.target.value)
                          } >
                          <option value=''>Select your state...</option>
                          {
                            Object.values(US_STATES)
                            .filter(s => s.templateId)
                            .sort((a, b) => a.stateName.localeCompare(b.stateName))
                            .map(s => (
                              <option key={s.stateName} value={s.stateName}>{s.stateName}</option>
                            ))
                          }
                        </select>
                      </div>
                    </div>
                    <div className="g-space-2"></div>
                  </div>
                  :
                  <p>
                    This evaluation will not be associated with your
                    personal information in any way.
                  </p>
              }
              <div className='question-set-display'>
                {
                  (questionSets && questionSets.length) &&
                  questionSets.map((set, index) => (
                    <div key={index} className='question-set'>
                      <h4>{set.name}</h4>
                      <p>{set.description}</p>
                      {
                        (set.questions && Object.values(set.questions).length > 0) &&
                        Object.values(set.questions)
                          .sort((a, b) => a.order - b.order)
                          .map((q, index) => (
                            <div key={index} className='question-div'>
                              <EvalQuestion
                                question={q}
                                formData={formData}
                                saveData={saveData}
                              />
                              <div className='g-space-2'></div>
                            </div>
                          ))
                      }
                    </div>
                  ))
                }
              </div>
            </div>
            :
            <h4 className='loading-div'>
              Loading evaluation
              <ThreeDotsLoader small={true} />
            </h4>
        }
        {
          (evalData && questionSets && questionSets.length > 0) &&
          <div className='buttons'>
            <button
              className='g-button primary'
              onClick={submitEval}>
              Submit Evaluation
            </button>
          </div>
        }

      </div>
      <ModalAlert
        message={alertMessage}
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        onApprove={approveRef.current} />
      <ModalConfirm
        message={confirmMessage}
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        onApprove={approveRef.current} />
      <FullPageLoader show={loading} />
    </div>
  )
}

export default EvalForm